import { HERO_FEATURED_COUNT, HERO_TOTAL_ROUNDING_STEP } from '~/constants/landing';
import type { HeroProjectRow, LandingProjectStage } from '~/types/landing';

// Pure helpers for the public landing hero search widget (server endpoint + Hero.vue).

/** The subset of a /api/properties/v2/list item the widget reads. */
export type HeroSourceProject = {
  title?: { en?: string | null } | null;
  developer?: { en?: string | null } | null;
  address?: string | null;
  districts?: string[] | null;
  price?: { min?: number | string | null } | null;
  status?: string | null;
  predicted_completion_at?: string | null;
  planned_completion_at?: string | null;
  isExclusive?: boolean | null;
  is_exclusive?: boolean | number | string | null;
  slug?: string | null;
  developerSlug?: string | null;
};

const STATUS_CODE_TO_STAGE: Record<string, LandingProjectStage> = {
  LAUNCH: 'pre-launch',
  PRELAUNCH: 'pre-launch',
  EOI: 'eoi',
  ON_SALE: 'on-sale',
  SOLD_OUT: 'sold-out',
  COMPLETED: 'ready',
  READY: 'ready',
};

// Secondary-market / distress listings never belong in the off-plan public widget.
const RESALE_OR_DISTRESS = /\b(resale|distress)/i;

const isTruthyFlag = (v: unknown): boolean =>
  v === true || v === 1 || (typeof v === 'string' && (v === '1' || v.toLowerCase() === 'true'));

export const isExclusiveProject = (p: HeroSourceProject): boolean =>
  isTruthyFlag(p.isExclusive) || isTruthyFlag(p.is_exclusive);

export const startingPriceOf = (p: HeroSourceProject): number => {
  const raw = p.price?.min;
  if (raw == null) return 0;
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const stageFromStatusCode = (code: string | null | undefined): LandingProjectStage | null =>
  STATUS_CODE_TO_STAGE[(code ?? '').toUpperCase().trim()] ?? null;

/** "Q4 2027" for a handover in the current quarter or later; null for past/missing dates. */
export const handoverQuarter = (raw: string | null | undefined, now: Date = new Date()): string | null => {
  if (!raw) return null;
  const quarterLabel = /^Q([1-4]) (\d{4})$/.exec(raw);
  const d = quarterLabel ? null : new Date(raw);
  if (d && Number.isNaN(d.getTime())) return null;
  const quarter = quarterLabel ? Number(quarterLabel[1]) : Math.floor(d!.getUTCMonth() / 3) + 1;
  const year = quarterLabel ? Number(quarterLabel[2]) : d!.getUTCFullYear();
  const nowQuarter = Math.floor(now.getUTCMonth() / 3) + 1;
  if (year * 4 + quarter < now.getUTCFullYear() * 4 + nowQuarter) return null;
  return `Q${quarter} ${year}`;
};

const areaOf = (p: HeroSourceProject): string => {
  if (Array.isArray(p.districts) && p.districts[0]) return String(p.districts[0]);
  return p.address?.split(',')[0]?.trim() ?? '';
};

/** Whitelisted display row, or null when the project must not be shown publicly (exclusive, resale, no price). */
export const toHeroProjectRow = (p: HeroSourceProject, now: Date = new Date()): HeroProjectRow | null => {
  if (isExclusiveProject(p)) return null;
  const name = p.title?.en?.trim() ?? '';
  const startingPriceAed = startingPriceOf(p);
  if (!name || !startingPriceAed || RESALE_OR_DISTRESS.test(name)) return null;
  return {
    name,
    developer: p.developer?.en?.trim() ?? '',
    area: areaOf(p),
    startingPriceAed,
    handover: handoverQuarter(p.predicted_completion_at ?? p.planned_completion_at, now),
    stage: stageFromStatusCode(p.status),
    developerSlug: p.developerSlug ?? '',
    projectSlug: p.slug ?? '',
  };
};

const rowKey = (row: HeroProjectRow): string => `${row.developerSlug}/${row.projectSlug}/${row.name}`.toLowerCase();

const matchesPreference = (row: HeroProjectRow, developer: string, area: string): boolean =>
  row.developer.toLowerCase().includes(developer.toLowerCase()) && row.area.toLowerCase().includes(area.toLowerCase());

/**
 * Featured rows: preferred developer-in-area first, topped up with the other candidates in order.
 * Exclusive / resale / unpriced candidates are dropped defensively; duplicates collapse; at most `max`.
 */
export const selectFeaturedProjects = (
  candidates: HeroSourceProject[],
  prefer: { developer: string; area: string },
  max: number = HERO_FEATURED_COUNT,
  now: Date = new Date(),
): HeroProjectRow[] => {
  const seen = new Set<string>();
  const rows: HeroProjectRow[] = [];
  for (const candidate of candidates) {
    const row = toHeroProjectRow(candidate, now);
    if (!row || seen.has(rowKey(row))) continue;
    seen.add(rowKey(row));
    rows.push(row);
  }
  const preferred = rows.filter((r) => matchesPreference(r, prefer.developer, prefer.area));
  const rest = rows.filter((r) => !matchesPreference(r, prefer.developer, prefer.area));
  return [...preferred, ...rest].slice(0, max);
};

/** Catalogue total rounded DOWN to the step with a "+" ("1,284" → "1,200+"); exact below one step; null when unknown. */
export const catalogueTotalLabel = (
  total: number | null | undefined,
  formatNumber: (n: number) => string = (n) => n.toLocaleString('en-US'),
): string | null => {
  if (total == null || !Number.isFinite(total) || total <= 0) return null;
  if (total < HERO_TOTAL_ROUNDING_STEP) return formatNumber(Math.floor(total));
  return `${formatNumber(Math.floor(total / HERO_TOTAL_ROUNDING_STEP) * HERO_TOTAL_ROUNDING_STEP)}+`;
};

export type HeroResultState = 'loading' | 'rows' | 'no-matches' | 'featured-unavailable';

/** "No matches" only when the visitor's own search/filter returned zero rows — never the default state. */
export const heroResultState = (s: {
  isSearching: boolean;
  searchPending: boolean;
  searchRowCount: number;
  featuredPending: boolean;
  featuredRowCount: number;
}): HeroResultState => {
  if (s.isSearching) {
    if (s.searchRowCount > 0) return 'rows';
    return s.searchPending ? 'loading' : 'no-matches';
  }
  if (s.featuredRowCount > 0) return 'rows';
  return s.featuredPending ? 'loading' : 'featured-unavailable';
};

/** "From AED 1.2M" price figure. */
export const formatAedShort = (n: number): string => {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `AED ${m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (n >= 1_000) return `AED ${Math.round(n / 1_000)}K`;
  return `AED ${n}`;
};
